import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Live countdown overlay for audience display — supports custom design */
function CountdownOverlay({ target, message, fg, fontFamily, visible, dur, design }) {
  const [display, setDisplay] = useState('');
  const d = design || {};

  useEffect(() => {
    const tick = () => {
      const now = new Date();
      const [h, m] = target.split(':').map(Number);
      const t = new Date(); t.setHours(h, m, 0, 0);
      if (t <= now) t.setDate(t.getDate() + 1);
      const diff = Math.max(0, Math.floor((t - now) / 1000));
      const hrs = Math.floor(diff / 3600);
      const mins = Math.floor((diff % 3600) / 60);
      const secs = diff % 60;
      const sep = d.separatorStyle || ':';
      setDisplay(hrs > 0 ? `${hrs}${sep}${mins.toString().padStart(2, '0')}${sep}${secs.toString().padStart(2, '0')}` : `${mins}${sep}${secs.toString().padStart(2, '0')}`);
      if (diff === 0) { try { new Audio('https://cdn.freesound.org/previews/411/411089_5121236-lq.mp3').play(); } catch(e) { /* */ } }
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [target, d.separatorStyle]);

  const timerColor = d.color || fg;
  const timerFont = d.font || 'system-ui, sans-serif';
  const timerSize = d.fontSize || 120;
  const timerWeight = d.fontWeight || 700;
  const spacing = d.letterSpacing || 4;
  const shadow = d.shadowIntensity || 40;
  const glowOn = d.glowEnabled || false;
  const glowColor = d.glowColor || '#3B82F6';
  const msgColor = d.messageColor || fg;
  const msgSize = d.messageFontSize || 32;
  const msgOpacity = d.messageOpacity || 0.5;
  const msgFont = d.messageFont || fontFamily || "'Georgia', serif";

  return (
    <div data-testid="countdown-overlay" style={{
      position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', zIndex: 10,
      opacity: visible ? 1 : 0,
      transition: `opacity ${dur}s ease-in-out`,
    }}>
      <p style={{
        color: timerColor, fontSize: `${timerSize}px`, fontWeight: timerWeight,
        fontFamily: timerFont, letterSpacing: `${spacing}px`,
        textShadow: glowOn
          ? `0 0 ${shadow}px ${glowColor}, 0 0 ${shadow * 2}px ${glowColor}40, 0 0 ${shadow * 3}px ${glowColor}20`
          : `0 4px ${shadow}px rgba(0,0,0,0.8)`,
        fontVariantNumeric: 'tabular-nums',
      }}>{display}</p>
      {message && (
        <p style={{
          color: msgColor, opacity: msgOpacity, fontSize: `${msgSize}px`, fontWeight: 400,
          fontFamily: msgFont, marginTop: '3vh', fontStyle: 'italic',
        }}>{message}</p>
      )}
    </div>
  );
}

export default function PresenterOutput() {
  const [searchParams] = useSearchParams();
  const userId = searchParams.get('uid') || '';
  const mode = searchParams.get('mode') || 'audience'; // audience, stage, confidence
  const [state, setState] = useState({
    is_live: false, is_blank: true,
    output_text: '', output_subtext: '',
    show_lower_third: false, lower_third_text: '',
    background_url: '', background_color: '#000000', background_opacity: 0.3,
    background_media_type: 'image',
    font_family: 'Georgia, serif', font_size: 48, font_weight: 600,
    text_color: '#ffffff', text_align: 'center', text_position: 'center',
    line_height: 1.4, text_shadow: true,
    transition_type: 'fade', transition_duration: 0.5,
    logo_url: '', logo_position: 'bottom-right', logo_size: 60, logo_opacity: 0.8,
  });
  const [visible, setVisible] = useState(false);
  const prevTextRef = useRef('');

  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'pp_live_state') {
        try { triggerTransition(JSON.parse(e.newValue)); } catch (err) { /* ignore */ }
      }
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  useEffect(() => {
    try {
      const stored = localStorage.getItem('pp_live_state');
      if (stored) { const p = JSON.parse(stored); setState(p); setVisible(true); }
    } catch (err) { /* ignore */ }
  }, []);

  const pollRef = useRef(null);
  useEffect(() => {
    if (!userId) return;
    const poll = async () => {
      try { const r = await axios.get(`${API}/propresenter/live/state?user_id=${userId}`); triggerTransition(r.data); } catch (err) { /* ok */ }
    };
    poll();
    pollRef.current = setInterval(poll, 2000);
    return () => clearInterval(pollRef.current);
  }, [userId]);

  const triggerTransition = (ns) => {
    const tt = ns.transition_type || state.transition_type || 'fade';
    const dur = ns.transition_duration || state.transition_duration || 0.5;
    if (tt === 'cut' || dur === 0) { setState(ns); setVisible(true); return; }
    if (prevTextRef.current !== ns.output_text) {
      setVisible(false);
      setTimeout(() => { setState(ns); setVisible(true); prevTextRef.current = ns.output_text; }, dur * 500);
    } else { setState(ns); }
  };

  const dur = state.transition_duration || 0.5;
  const bg = state.slide_background_color || state.background_color || '#000000';
  const fg = state.text_color || '#ffffff';
  const alignItems = state.text_position === 'top' ? 'flex-start' : state.text_position === 'bottom' ? 'flex-end' : 'center';
  const textAlign = state.text_align || 'center';
  const justifyContent = textAlign === 'left' ? 'flex-start' : textAlign === 'right' ? 'flex-end' : 'center';

  const logoStyle = (() => {
    const base = { width: `${state.logo_size || 60}px`, opacity: state.logo_opacity || 0.8, position: 'absolute', zIndex: 25 };
    const pos = state.logo_position || 'bottom-right';
    if (pos === 'top-left') return { ...base, top: 24, left: 24 };
    if (pos === 'top-right') return { ...base, top: 24, right: 24 };
    if (pos === 'bottom-left') return { ...base, bottom: 24, left: 24 };
    if (pos === 'bottom-right') return { ...base, bottom: 24, right: 24 };
    return { ...base, top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
  })();

  // Parse formatting markers
  const renderText = (text) => {
    if (!text) return null;
    let parts = text;
    // Handle [highlight] markers
    parts = parts.replace(/\[highlight\](.*?)\[\/highlight\]/g, '{{HL_START}}$1{{HL_END}}');
    // Handle **bold**
    parts = parts.replace(/\*\*(.*?)\*\*/g, '{{B_START}}$1{{B_END}}');
    // Handle _italic_
    parts = parts.replace(/_(.*?)_/g, '{{I_START}}$1{{I_END}}');

    const segments = parts.split(/({{HL_START}}|{{HL_END}}|{{B_START}}|{{B_END}}|{{I_START}}|{{I_END}})/);
    let inHighlight = false, inBold = false, inItalic = false;
    const result = [];
    segments.forEach((seg, i) => {
      if (seg === '{{HL_START}}') { inHighlight = true; return; }
      if (seg === '{{HL_END}}') { inHighlight = false; return; }
      if (seg === '{{B_START}}') { inBold = true; return; }
      if (seg === '{{B_END}}') { inBold = false; return; }
      if (seg === '{{I_START}}') { inItalic = true; return; }
      if (seg === '{{I_END}}') { inItalic = false; return; }
      if (seg) {
        result.push(
          <span key={i} style={{
            backgroundColor: inHighlight ? 'rgba(255,215,0,0.3)' : 'transparent',
            fontWeight: inBold ? 800 : undefined,
            fontStyle: inItalic ? 'italic' : undefined,
            padding: inHighlight ? '0 4px' : undefined,
            borderRadius: inHighlight ? '2px' : undefined,
          }}>{seg}</span>
        );
      }
    });
    return result.length > 0 ? result : text;
  };

  return (
    <div data-testid="presenter-output-screen" style={{ position: 'fixed', inset: 0, backgroundColor: bg, overflow: 'hidden', cursor: 'none' }}>
      {/* Background */}
      {mode === 'audience' && state.background_url && (state.background_media_type === 'video' ? (
        <video src={state.background_url} autoPlay loop muted playsInline style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', opacity: state.background_opacity || 0.3 }} />
      ) : (
        <img src={state.background_url} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', opacity: state.background_opacity || 0.3 }} />
      ))}

      {/* AUDIENCE MODE */}
      {mode === 'audience' && (<>
        {state.show_countdown && state.countdown_target ? (
          <>
            {state.cd_design?.bgType === 'video' && state.cd_design?.bgVideoUrl && (
              <video src={state.cd_design.bgVideoUrl} autoPlay loop muted playsInline style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', opacity: 1 - (state.cd_design.bgOverlayOpacity || 0.6), zIndex: 5 }} />
            )}
            {state.cd_design?.bgType === 'video' && <div style={{ position: 'absolute', inset: 0, backgroundColor: 'black', opacity: state.cd_design?.bgOverlayOpacity || 0.6, zIndex: 6 }} />}
            <CountdownOverlay target={state.countdown_target} message={state.countdown_message || 'Service begins soon'} fg={fg} fontFamily={state.font_family} visible={visible} dur={dur} design={state.cd_design} />
          </>
        ) : (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems, justifyContent, padding: `${state.padding_y || 5}vh ${state.padding_x || 10}vw`, opacity: visible ? 1 : 0, transition: state.transition_type === 'cut' ? 'none' : `opacity ${dur}s ease-in-out`, zIndex: 10 }}>
            {!state.is_blank && state.output_text ? (
              <div style={{ textAlign, maxWidth: '100%' }}>
                <p data-testid="output-main-text" style={{ color: fg, fontSize: `${state.font_size || 48}px`, fontWeight: state.font_weight || 600, lineHeight: state.line_height || 1.4, whiteSpace: 'pre-wrap', textShadow: state.text_shadow ? '0 2px 24px rgba(0,0,0,0.9), 0 0 60px rgba(0,0,0,0.5)' : 'none', fontFamily: state.font_family || "'Georgia', serif" }}>{renderText(state.output_text)}</p>
                {state.output_subtext && (<p data-testid="output-sub-text" style={{ color: fg, opacity: 0.45, fontSize: `${Math.max((state.font_size || 48) * (state.subtext_size_ratio || 0.4), 14)}px`, marginTop: '2.5vh', fontFamily: state.font_family || "'Georgia', serif", fontStyle: 'italic' }}>{state.output_subtext}</p>)}
              </div>
            ) : null}
          </div>
        )}
        {state.logo_url && <img src={state.logo_url} alt="Logo" data-testid="output-logo" style={logoStyle} />}
        {state.show_lower_third && state.lower_third_text && (
          <div data-testid="output-lower-third" style={{ position: 'absolute', bottom: 0, left: 0, right: 0, backgroundColor: state.lt_bg_image ? 'transparent' : (state.lt_bg_color || 'rgba(27,58,107,0.95)'), backgroundImage: state.lt_bg_image ? `url(${state.lt_bg_image})` : 'none', backgroundSize: 'cover', backgroundPosition: 'center', backdropFilter: 'blur(12px)', opacity: state.lt_opacity || 1, padding: `${state.lt_padding_y || 20}px ${state.lt_padding_x || 48}px`, zIndex: 20, animation: `${state.lt_animation || 'slideUp'} 0.5s ease-out` }}>
            <p style={{ color: state.lt_text_color || '#ffffff', fontSize: `${state.lt_font_size || 24}px`, fontWeight: state.lt_font_weight || 600, fontFamily: state.lt_font_family || 'system-ui, sans-serif', margin: 0 }}>{state.lower_third_text}</p>
          </div>
        )}
      </>)}

      {/* STAGE MODE — Shows current text large + chords + next slide preview */}
      {mode === 'stage' && (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', padding: '3vh 5vw', zIndex: 10 }} data-testid="stage-mode">
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            {state.output_text ? (<>
              <p style={{ color: '#F8FAFC', fontSize: '36px', fontWeight: 600, lineHeight: 1.5, whiteSpace: 'pre-wrap', fontFamily: "'Georgia', serif" }}>{state.output_text}</p>
              {state.output_subtext && <p style={{ color: '#64748B', fontSize: '16px', marginTop: '1vh', fontStyle: 'italic' }}>{state.output_subtext}</p>}
            </>) : <p style={{ color: '#334155', fontSize: '24px' }}>Waiting for content...</p>}
          </div>
          <div style={{ borderTop: '1px solid #27272A', paddingTop: '2vh', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
            <div style={{ flex: 1 }}>
              <p style={{ color: '#475569', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>Next</p>
              <p style={{ color: '#64748B', fontSize: '16px', fontFamily: "'Georgia', serif" }}>—</p>
            </div>
            <div style={{ textAlign: 'right' }}>
              <p style={{ color: '#3B82F6', fontSize: '14px', fontFamily: "'JetBrains Mono', monospace", fontWeight: 700 }}>STAGE</p>
            </div>
          </div>
        </div>
      )}

      {/* CONFIDENCE MODE — Shows current slide centered with reference below */}
      {mode === 'confidence' && (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '5vh 8vw', zIndex: 10 }} data-testid="confidence-mode">
          {state.output_text ? (<>
            <p style={{ color: '#F8FAFC', fontSize: '28px', fontWeight: 500, lineHeight: 1.6, whiteSpace: 'pre-wrap', fontFamily: "'Georgia', serif", textAlign: 'center' }}>{state.output_text}</p>
            {state.output_subtext && <p style={{ color: '#3B82F6', fontSize: '14px', marginTop: '2vh', fontStyle: 'italic', textAlign: 'center' }}>{state.output_subtext}</p>}
          </>) : <p style={{ color: '#334155', fontSize: '20px' }}>No content</p>}
          <div style={{ position: 'absolute', bottom: '2vh', right: '3vw' }}>
            <p style={{ color: '#8B5CF6', fontSize: '11px', fontFamily: "'JetBrains Mono', monospace", fontWeight: 700 }}>CONFIDENCE</p>
          </div>
        </div>
      )}

      <style>{`
        @keyframes slideUp { from { transform: translateY(100%); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
        @keyframes slideLeft { from { transform: translateX(-100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
        @keyframes slideRight { from { transform: translateX(100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes bounce { 0% { transform: translateY(100%); } 50% { transform: translateY(-8px); } 70% { transform: translateY(4px); } 100% { transform: translateY(0); } }
        * { margin: 0; padding: 0; } html, body { overflow: hidden; }
      `}</style>
    </div>
  );
}
